import { useMemo, useState } from "react";
import { PLANK_TYPES } from "./constants";
import TorkPicker from "./TorkPicker";

const emptyForm = () => ({
    orderId: "",
    sawedAt: "",
    dimension: "",
    moisture: "",
    materialId: "",
    pickupLocation: "",
    torkId: null,
    comment: "",
    quantities: PLANK_TYPES.reduce((acc, t) => ({ ...acc, [t]: 0 }), {})
});

const CreateOrderForm = ({ onSubmit, onClose, torks = [], noteTypes = [] }) => {
    const [form, setForm] = useState(emptyForm());
    const [showTorkPicker, setShowTorkPicker] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const totalQuantity = useMemo(
        () => Object.values(form.quantities).reduce((sum, n) => sum + (Number(n) || 0), 0),
        [form.quantities]
    );

    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
    const setQuantity = (type, value) => setForm(prev => ({
        ...prev,
        quantities: { ...prev.quantities, [type]: Math.max(0, parseInt(value || "0", 10)) }
    }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !form.orderId.trim() || totalQuantity <= 0) return;

        const quantities = {};
        Object.entries(form.quantities).forEach(([t, n]) => {
            if (Number(n) > 0) quantities[t] = Number(n);
        });

        setSubmitting(true);
        try {
            await onSubmit({
                orderId: form.orderId.trim(),
                quantities,
                package: {
                    sawedAt: form.sawedAt || null,
                    dimension: form.dimension || null,
                    moisture: form.moisture || null,
                    materialId: form.materialId || null
                },
                pickupLocation: form.pickupLocation || null,
                torkId: form.torkId || null,
                comment: form.comment || null
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <form
                onSubmit={handleSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
                onClick={e => e.stopPropagation()}
            >
                <h2 className="text-xl font-semibold mb-4">Ny torkorder</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Order-ID *">
                        <input className="input" value={form.orderId}
                            onChange={e => setField("orderId", e.target.value)} required />
                    </Field>
                    <Field label="Sågad datum">
                        <input type="date" className="input" value={form.sawedAt}
                            onChange={e => setField("sawedAt", e.target.value)} />
                    </Field>
                    <Field label="Dimension">
                        <input className="input" value={form.dimension} placeholder="62x100"
                            onChange={e => setField("dimension", e.target.value)} />
                    </Field>
                    <Field label="Fuktkvot">
                        <input className="input" value={form.moisture} placeholder="12%"
                            onChange={e => setField("moisture", e.target.value)} />
                    </Field>
                    <Field label="Material-ID">
                        <input className="input" value={form.materialId}
                            onChange={e => setField("materialId", e.target.value)} />
                    </Field>
                    <Field label="Fot">
                        <input className="input" value={form.pickupLocation} placeholder="102-20"
                            onChange={e => setField("pickupLocation", e.target.value)} />
                    </Field>
                    <Field label="Tork">
                        <button
                            type="button"
                            onClick={() => setShowTorkPicker(true)}
                            className="input text-left w-full text-gray-700 hover:bg-gray-50"
                        >
                            {form.torkId
                                ? torks.find(t => t.id === form.torkId)?.name || "–"
                                : <span className="text-gray-400">Välj tork…</span>}
                        </button>
                    </Field>
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Antal per typ</label>
                    <div className="flex flex-wrap gap-4">
                        {PLANK_TYPES.map(type => (
                            <div key={type} className="flex flex-col items-center">
                                <span className="text-sm text-gray-600 mb-1">{type}</span>
                                <input
                                    type="number" min="0"
                                    className="input w-20 text-center"
                                    value={form.quantities[type]}
                                    onChange={e => setQuantity(type, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                        Totalt {totalQuantity} post(er) skapas med samma order-ID.
                    </p>
                </div>

                <Field label="Kommentar" className="mt-4">
                    <textarea className="input" rows={2} value={form.comment}
                        onChange={e => setField("comment", e.target.value)} />
                </Field>

                <div className="flex justify-end gap-2 mt-6">
                    <button type="button" onClick={onClose} disabled={submitting}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50">
                        Avbryt
                    </button>
                    <button type="submit" disabled={submitting || totalQuantity <= 0 || !form.orderId.trim()}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {submitting ? "Skapar…" : "Skapa order"}
                    </button>
                </div>
            </form>

            {showTorkPicker && (
                <div onClick={e => e.stopPropagation()}>
                    <TorkPicker
                        torks={torks}
                        noteTypes={noteTypes}
                        value={form.torkId}
                        onChange={(id) => setField("torkId", id)}
                        onClose={() => setShowTorkPicker(false)}
                    />
                </div>
            )}
        </div>
    );
};

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        {children}
    </div>
);

export default CreateOrderForm;
